/**
 * RoleBasedActions Component
 *
 * A reusable component that renders action buttons based on user permissions.
 * Automatically shows/hides buttons and applies appropriate styling based on role.
 */

import {
  Add as AddIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Visibility as ViewIcon,
  Group as ManageIcon
} from '@mui/icons-material'
import { Box, Button, ButtonGroup, Tooltip } from '@mui/material'
import React from 'react'

import { OfficeWithRole } from '../../utils/profileHelpers'
import {
  getActionButtonVisibility,
  getButtonVariant
} from '../../utils/rolePermissions'

export interface RoleBasedActionsProps {
  /** Office with user role information */
  office: OfficeWithRole
  /** Callback for view action */
  onView?: () => void
  /** Callback for edit action */
  onEdit?: () => void
  /** Callback for add desk action */
  onAddDesk?: () => void
  /** Callback for invite users action */
  onInvite?: () => void
  /** Callback for manage members action */
  onManageMembers?: () => void
  /** Layout variant */
  variant?: 'group' | 'spaced'
  /** Size of buttons */
  size?: 'small' | 'medium' | 'large'
  /** Whether to show button text or just icons */
  showText?: boolean
  /** Whether the invite button should be disabled */
  inviteDisabled?: boolean
  /** Tooltip to show when invite button is disabled */
  inviteDisabledTooltip?: string
}

/**
 * RoleBasedActions - Renders action buttons based on user permissions
 *
 * Automatically determines which buttons to show based on the user's role
 * and applies appropriate styling. Supports different layouts and sizes.
 */
const RoleBasedActions: React.FC<RoleBasedActionsProps> = ({
  office,
  onView,
  onEdit,
  onAddDesk,
  onInvite,
  onManageMembers,
  variant = 'spaced',
  size = 'medium',
  showText = true,
  inviteDisabled = false,
  inviteDisabledTooltip = ''
}) => {
  const visibility = getActionButtonVisibility(office)
  const { userRole } = office

  // Create button configurations
  const buttons = [
    {
      key: 'view',
      show: visibility.showViewButton && onView,
      variant: getButtonVariant('view', userRole),
      icon: <ViewIcon />,
      text: 'View Details',
      onClick: onView,
      disabled: false,
      tooltip: ''
    },
    {
      key: 'edit',
      show: visibility.showEditButton && onEdit,
      variant: getButtonVariant('edit', userRole),
      icon: <EditIcon />,
      text: 'Edit Office',
      onClick: onEdit,
      disabled: false,
      tooltip: ''
    },
    {
      key: 'addDesk',
      show: visibility.showAddDeskButton && onAddDesk,
      variant: getButtonVariant('create', userRole),
      icon: <AddIcon />,
      text: 'Add Desk',
      onClick: onAddDesk,
      disabled: false,
      tooltip: ''
    },
    {
      key: 'invite',
      show: visibility.showInviteButton && onInvite,
      variant: getButtonVariant('invite', userRole),
      icon: <PersonAddIcon />,
      text: 'Invite Users',
      onClick: onInvite,
      disabled: inviteDisabled,
      tooltip: inviteDisabledTooltip
    },
    {
      key: 'manage',
      show: visibility.showManageMembersButton && onManageMembers,
      variant: getButtonVariant('manage', userRole),
      icon: <ManageIcon />,
      text: 'Manage Members',
      onClick: onManageMembers,
      disabled: false,
      tooltip: ''
    }
  ].filter(button => button.show)

  // Don't render anything if no buttons are visible
  if (buttons.length === 0) {
    return null
  }

  // Helper to wrap button with tooltip if needed
  const wrapWithTooltip = (button: any, element: React.ReactElement) => {
    if (button.disabled && button.tooltip) {
      return (
        <Tooltip key={button.key} title={button.tooltip} arrow>
          <span>{element}</span>
        </Tooltip>
      )
    }
    return element
  }

  // Render as button group
  if (variant === 'group') {
    return (
      <ButtonGroup size={size} variant='outlined'>
        {buttons.map(button =>
          wrapWithTooltip(
            button,
            <Button
              key={button.key}
              variant={button.variant}
              startIcon={button.icon}
              onClick={button.onClick}
              disabled={button.disabled}
            >
              {showText ? button.text : ''}
            </Button>
          )
        )}
      </ButtonGroup>
    )
  }

  // Render as spaced buttons
  return (
    <Box display='flex' gap={1} flexWrap='wrap'>
      {buttons.map(button =>
        wrapWithTooltip(
          button,
          <Button
            key={button.key}
            variant={button.variant}
            size={size}
            startIcon={button.icon}
            onClick={button.onClick}
            disabled={button.disabled}
          >
            {showText ? button.text : ''}
          </Button>
        )
      )}
    </Box>
  )
}

export default RoleBasedActions
